import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useVerificationFailedToast } from '@/components/features/fair-play/hooks/useVerificationFailedToast';
import Toast from '@/components/ui/Toast/toast';
import { useSocketEvent } from '@/hooks/socket/useSocketEvent';
// Note: Block access to the fair-play pages. Restore when re-enabled below.
// import { useRouter } from '@/i18n/routing';
import openModal from '@/modals/utils/openModal';
import { QueriesKeys } from '@/queries/queriesKeys';
import { useAppSelector } from '@/redux/hooks';

interface RoundVerificationPayload {
  roundId: string;
  gameId: string;
}

export function useGameRoundVerificationSocket() {
  const queryClient = useQueryClient();
  const userSession = useAppSelector(state => state.user.userSession);
  const t = useTranslations('fair-play.verify-win');
  const showVerificationFailedToast = useVerificationFailedToast();
  // const router = useRouter();
  const isSessionActive = Boolean(userSession);

  // Refresh the wins list so its verification state is current before the toast appears
  const invalidateWins = () => queryClient.invalidateQueries({ queryKey: [QueriesKeys.roundsWithWins] });

  useSocketEvent<RoundVerificationPayload>(
    'game.round.verification.success',
    async ({ roundId, gameId }) => {
      Toast({
        type: 'success',
        title: t('verification-success.title'),
        description: t('verification-success.description'),
        // Note: Block access to the fair-play pages.
        // After approval, when we add it to the production environment,
        // uncomment this code block.
        // rejectButtonText: t('verification-success.fair-play-button'),
        // onReject: () => router.push('/fair-play'),
        acceptButtonText: t('verification-success.share-win-button'),
        onAccept: () => openModal('win-details', { roundId, gameId }),
      });
      await invalidateWins();
    },
    isSessionActive,
  );

  useSocketEvent<RoundVerificationPayload>(
    'game.round.verification.failed',
    async () => {
      showVerificationFailedToast();
      await invalidateWins();
    },
    isSessionActive,
  );
}
